// Conversazioni (tela, artboard 46): l'elenco delle chat a sinistra, come in ChatGPT, e la chat
// aperta a destra. Una Conversazione è una sessione in modo «chat»: risponde il pop scelto, che
// si cambia dall'intestazione; nessun piano, nessun team. Chiusa, si riprende scrivendo.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import type { AgentProfile } from '../../lib/api';
import type { Session } from '../../types';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Pop } from '../../components/Pop';
import { RelativeTime } from '../../components/RelativeTime';
import { useStore } from '../../store/StoreProvider';
import { ChatPane } from './ChatPane';

export function Conversations() {
  const { state, openModal } = useStore();
  const { id } = useParams();
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);

  useEffect(() => {
    api.profiles().then(setProfiles, () => setProfiles([]));
  }, []);

  const chats = useMemo(
    () =>
      state.sessions
        .filter((s) => s.mode === 'chat')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [state.sessions],
  );
  const shown = chats.filter((s) => s.title.toLowerCase().includes(query.trim().toLowerCase()));
  const open = chats.find((s) => s.id === id);
  const avatar = (s: Session) =>
    profiles.find((p) => p.id === s.chatProfile)?.avatar ?? 'project-manager-01';
  const last = (s: Session) => {
    const mine = state.messages.filter((m) => m.sessionId === s.id);
    return mine[mine.length - 1]?.text ?? s.requestText;
  };
  const start = () =>
    openModal({ kind: 'new-request', title: 'Nuova richiesta', payload: { mode: 'chat' } });

  return (
    <div className="cv-page" data-open={open ? 'chat' : 'list'}>
      <section className="cv-list" aria-label="Elenco delle conversazioni">
        <div className="cv-list-head">
          <h1 className="t-h2" style={{ margin: 0 }}>
            Conversazioni
          </h1>
          <Button variant="primary" icon="plus" full onClick={start}>
            Nuova conversazione
          </Button>
          <label className="cv-search">
            <Icon name="search" size={15} />
            <input
              aria-label="Cerca nelle conversazioni"
              placeholder="Cerca"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </div>
        {chats.length === 0 ? (
          <p className="t-small dim" style={{ padding: 16, margin: 0 }}>
            Nessuna conversazione. Con «Nuova conversazione» scegli un pop e gli scrivi.
          </p>
        ) : (
          <ul className="cv-items">
            {shown.map((s) => (
              <li key={s.id}>
                <a
                  href={`/conversazioni/${s.id}`}
                  aria-current={s.id === id ? 'page' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    nav(`/conversazioni/${s.id}`);
                  }}
                  className="cv-item"
                >
                  <Pop avatar={avatar(s)} size={32} />
                  <span className="cv-item-text">
                    <span className="t-small cv-ellipsis" style={{ fontWeight: 600 }}>
                      {s.title}
                    </span>
                    <span className="t-caption dim cv-ellipsis">{last(s)}</span>
                  </span>
                  <RelativeTime at={s.updatedAt} />
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
      {open ? (
        <ChatPane session={open} profiles={profiles} onBack={() => nav('/conversazioni')} />
      ) : (
        <section className="cv-empty t-small dim">
          <Pop avatar="mail-clerk-01" size={130} />
          <span className="t-h2" style={{ color: 'var(--text)' }}>
            Scrivi ai pop
          </span>
          Scegli una conversazione dall'elenco, o iniziane una nuova.
        </section>
      )}
    </div>
  );
}
